import { faroCoreApiClient } from "@api/faro-core-api-client";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { useFileUploadContext } from "@context-providers/file-upload/file-uploads-context";
import { isCanceledUploadFile, isUploadedFile, isUploadFailedFile } from "@custom-types/file-upload-type-guards";
import { BackgroundTaskState } from "@faro-lotv/service-wires";
import { doUpload, validateFile } from "@hooks/file-upload-utils";
import { useToast } from "@flat-ui";
import { DateTimeUtils } from "@stellar/utils";
import { useAppDispatch } from "@store/store-helper";
import { setOne } from "@store/upload-tasks/upload-tasks-slice";
import { checkMagicFileHeader } from "@utils/file-utils";
import { sentryCaptureError } from "@utils/sentry-utils";
import { getRealFile } from "@utils/file-ext";
import { uniqueId } from "es-toolkit/compat";
/** get corresponding bucket so we can chart easily the Upload speed distribution (speed buckets e.g. for current month) */
function getUploadSpeedBucket(uploadSpeedMBps) {
  if (uploadSpeedMBps <= 2) {
    return "0-2";
  }
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  if (uploadSpeedMBps <= 5) {
    return "2-5";
  }
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  if (uploadSpeedMBps <= 10) {
    return "5-10";
  }
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  if (uploadSpeedMBps <= 20) {
    return "10-20";
  }
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  if (uploadSpeedMBps <= 50) {
    return "20-50";
  }
  return ">50";
}

/** A hook to upload a file */
export function useFileUpload() {
  const {
    handleErrorWithToast
  } = useErrorContext();
  const {
    uploadManager
  } = useFileUploadContext();
  const {
    openToast
  } = useToast();
  const dispatch = useAppDispatch();
  async function uploadSingleFile({
    file,
    onUploadStart,
    onUploadProgress,
    onUploadComplete,
    context,
    isSilentError = false
  }) {
    if (!file.type) {
      // file.type is used to determine the the content-type header of the upload request
      // and must be set for some storage providers.
      openToast({
        title: "File type Missing",
        message: "The selected file is not valid type to upload",
        variant: "error"
      });
      return false;
    }
    try {
      await checkMagicFileHeader(file);
      onUploadStart();

      // Fetch upload request details
      const uploadRequest = await faroCoreApiClient.file.generateSignedUploadUrlWithHeaders({
        contentType: file.type,
        companyId: context.companyId ?? "",
        projectId: context.projectId ?? "",
        memberId: context.userId ?? ""
      });

      // Make sure, FileExt is uploaded correctly
      const blob = getRealFile(file);

      // Upload the file
      const result = await faroCoreApiClient.file.uploadAsset({
        method: uploadRequest.method,
        uploadUrl: uploadRequest.uploadUrl,
        file: blob,
        type: file.type,
        headers: uploadRequest.headers,
        onUploadProgress
      });
      if (!result.isUploaded) {
        throw new Error(`Failed to upload <var>${file.name}</var>: ${result.status} ${result.statusText}`);
      }
      const downloadUrl = uploadRequest.uploadUrl.split("?")[0];

      // Callback with the new file url and file
      onUploadComplete(downloadUrl, context);
      return true;
    } catch (error) {
      const handleError = isSilentError ? sentryCaptureError : handleErrorWithToast;
      handleError({
        id: `uploadSingleFile-${Date.now().toString()}`,
        title: "Error uploading file",
        error
      });
      return false;
    }
  }
  async function uploadFileWithChunks({
    file: file_0,
    onUploadStart: onUploadStart_0,
    onUploadProgress: onUploadProgress_0,
    onUploadComplete: onUploadComplete_0,
    context: context_0,
    isSilentError: isSilentError_0 = false
  }) {
    if (!file_0.type) {
      // file.type is used to determine the the content-type header of the upload request
      // and must be set for some storage providers.
      openToast({
        title: "File type Missing",
        message: "The selected file is not valid type to upload",
        variant: "error"
      });
      return false;
    }
    try {
      await checkMagicFileHeader(file_0);
      onUploadStart_0();
      const uploadResult = await doUpload({
        file: file_0,
        // The CoreAPI endpoint to upload files always requires the project and company ID field.
        // If the project ID or company ID is not defined then just pass an empty string.
        // NOTE: CoreAPI doesn't seem to allow an empty string any more; see SphereDropzone.
        projectId: context_0.projectId ?? "",
        companyId: context_0.companyId ?? "",
        memberId: context_0.userId ?? "",
        onUploadProgress: onUploadProgress_0
      });
      onUploadComplete_0(uploadResult.downloadUrl, context_0);
      return true;
    } catch (error_0) {
      const handleError_0 = isSilentError_0 ? sentryCaptureError : handleErrorWithToast;
      handleError_0({
        id: `uploadFileWithChunks-${Date.now().toString()}`,
        title: "Error uploading file",
        error: error_0
      });
      return false;
    }
  }
  async function uploadMultipleFiles({
    files,
    finalizer,
    onUploadStart: onUploadStart_1,
    onUploadProgress: onUploadProgress_1,
    onUploadComplete: onUploadComplete_1,
    context: context_1
  }) {
    onUploadStart_1();

    // Adjust the number of concurrent file and chunk uploads to best utilize the bandwidth.
    uploadManager.setMaxConcurrentUploadsAuto(files);
    const uploadT0 = performance.now();
    const responses = await Promise.all(files.map(file_1 =>
    // checkMagicFileHeader() is called in CoreFileUploader.doUpload().
    new Promise(resolve => {
      uploadManager.startFileUpload({
        file: file_1,
        context: context_1,
        coreApiClient: faroCoreApiClient,
        finalizer,
        onUploadCompleted: (id, fileName, fileSize, fileType, downloadUrl_0, md5) => {
          resolve({
            id,
            fileName,
            fileSize,
            fileType,
            downloadUrl: downloadUrl_0,
            md5
          });
        },
        onUploadFailed: (id_0, fileName_0, error_1) => {
          resolve({
            id: id_0,
            fileName: fileName_0,
            errorMessage: error_1.message
          });
        },
        onUploadCanceled: (id_1, fileName_1) => {
          resolve({
            id: id_1,
            fileName: fileName_1
          });
        },
        onUploadUpdated: (_, progress) => {
          onUploadProgress_1(progress);
        }
      });
    })));
    const uploadT1 = performance.now();
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    const bytesInMB = 1024 * 1024;
    const uploadsTimeSecs = (uploadT1 - uploadT0) / DateTimeUtils.MILLISECONDS_IN_SECOND;
    const successUploads = responses.filter(isUploadedFile);
    const successUploadsSize = successUploads.reduce((sum, file_2) => sum + file_2.fileSize, 0);
    const successUploadSpeedMBps = successUploadsSize / bytesInMB / uploadsTimeSecs;
    const uploadedResponse = {
      successful: successUploads,
      failed: responses.filter(isUploadFailedFile),
      canceled: responses.filter(isCanceledUploadFile),
      uploadSizeMB: Number((successUploadsSize / bytesInMB).toFixed(2)),
      uploadTimeSecs: Number(uploadsTimeSecs.toFixed(2)),
      uploadSpeedMBps: Number(successUploadSpeedMBps.toFixed(2)),
      uploadSpeedBucketMBps: getUploadSpeedBucket(successUploadSpeedMBps)
    };
    onUploadComplete_1(uploadedResponse, context_1);
    return true;
  }

  /** Used to add failed task in store */
  function addFailedUploadTaskInStore({
    fileName: fileName_2,
    errorMessage,
    context: context_2
  }) {
    const isoDate = new Date().toISOString();
    const fileUploadTask = {
      id: uniqueId(),
      fileName: fileName_2,
      createdAt: isoDate,
      status: BackgroundTaskState.failed,
      progress: 0,
      context: context_2,
      errorMessage
    };
    dispatch(setOne(fileUploadTask));
  }
  function validateAndAddFailedTask({
    file: file_3,
    allowedExtensions,
    maxFileSize,
    context: context_3
  }) {
    const validatedFile = validateFile({
      file: file_3,
      allowedExtensions,
      maxFileSize
    });
    if (!validatedFile.isValid) {
      openToast({
        title: validatedFile.message ?? "Invalid file",
        message: validatedFile.description,
        variant: "warning"
      });
      addFailedUploadTaskInStore({
        fileName: file_3.name,
        context: context_3,
        errorMessage: validatedFile.message ?? "Failed to import file"
      });
      return false;
    }
    return true;
  }
  return {
    uploadSingleFile,
    uploadFileWithChunks,
    uploadMultipleFiles,
    validateAndAddFailedTask
  };
}